/**
 * Persistent storage abstraction for parsed SHX font bytes.
 *
 * The project currently stores fonts client-side in IndexedDB; in the
 * future the same drawing + fonts will live entirely on a server (see
 * plan "indexed-painting-perlis.md" §Уточнения #1). The only way to
 * migrate safely is to keep *every* font persistence call behind this
 * interface so the runtime can swap `IndexedDbFontStore` for a future
 * `ServerFontStore` with a one-line change in the hydration wiring.
 *
 * ### Cancellation semantics
 *
 * Every method accepts an optional `AbortSignal`. Implementations MUST:
 * - reject with a `DOMException("Aborted", "AbortError")` if the signal
 *   is already aborted on entry,
 * - abort the underlying transaction and reject with an AbortError when
 *   the signal fires mid-flight.
 *
 * This matches the Fetch API contract and lets a future server-backed
 * store cancel in-flight HTTP uploads without the caller having to care
 * about the implementation.
 *
 * ### Storage key normalisation
 *
 * Implementations normalise the logical font name the same way
 * `FontRegistry.normalize` does (`trim().toLowerCase().replace(/\.shx$/, "")`)
 * so a round-trip of `"ROMANS.SHX"` and `"romans"` hits the same record.
 */

import type { ShxParseError } from "@dxf-ai-editor/shx-parser";

/**
 * Persistent store for the raw bytes of registered SHX fonts.
 *
 * Values are stored as `Uint8Array`. Callers are expected to parse the
 * bytes through `parseShxFont` (via `FontRegistry.register`) before
 * rendering — the store itself is format-agnostic and never inspects
 * the content.
 */
export interface FontStore {
  /**
   * Persist the bytes of a font under `name`. Overwrites any previously
   * saved bytes for the same normalised key.
   */
  save(name: string, bytes: Uint8Array, signal?: AbortSignal): Promise<void>;

  /**
   * Return every stored font in a single batch. Used at application
   * start by `hydrateFontsFromStore` to re-populate `FontRegistry`
   * from IndexedDB (or, in future, the server). Order is unspecified —
   * the `FontRegistry` normalises and sorts on its own.
   */
  loadAll(
    signal?: AbortSignal
  ): Promise<Array<{ name: string; bytes: Uint8Array }>>;

  /**
   * Remove a single font from the store. No-op if the key is absent.
   */
  remove(name: string, signal?: AbortSignal): Promise<void>;

  /**
   * Remove every font from the store. Used by the "clear fonts" admin
   * action in `FontManagerDialog` and by test setup/teardown.
   */
  clear(signal?: AbortSignal): Promise<void>;
}

/**
 * Discriminated error for store operations. Implementations should
 * return / wrap errors into one of these codes so the UI layer can
 * render a single translated message bank.
 */
export type FontStoreErrorCode =
  | "unavailable"
  | "quota-exceeded"
  | "aborted"
  | "unknown";

/** @public */
export type FontStoreError = {
  code: FontStoreErrorCode;
  message: string;
  cause?: unknown;
};

/**
 * Thrown from the `IndexedDbFontStore` constructor (or an equivalent
 * store's initialisation) when the browser does not expose the backing
 * API at all — e.g. Firefox private windows that disable IndexedDB,
 * legacy UAs without the global, or a misconfigured test environment.
 *
 * Callers (the App's hydration `useEffect`) should catch this and fall
 * back to a pure in-memory `FontRegistry` with a user-visible warning,
 * never crash the app.
 */
export class FontStoreUnavailableError extends Error {
  readonly code = "unavailable" as const;
  constructor(message: string) {
    super(message);
    this.name = "FontStoreUnavailableError";
  }
}

/**
 * Helper: normalise a font name to the same key used by `FontRegistry`
 * and every `FontStore` implementation. Exported so adapters don't
 * re-derive the rule (drift risk).
 */
export function normalizeFontKey(name: string): string {
  return name.trim().toLowerCase().replace(/\.shx$/u, "");
}

/**
 * Re-exported for convenience so callers that handle store errors can
 * also narrow on parser errors from `FontRegistry.register` without
 * importing from two packages.
 */
export type { ShxParseError };
